import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UsersRound, Search, X, Settings2, Lock, Clock, Loader2, ArrowLeft, Crown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const GroupsChip = ({ active, onClick }) => (
  <button data-testid="market-cat-groups" onClick={onClick}
    className={`shrink-0 px-3 py-1.5 text-xs rounded-full transition-all border flex items-center gap-1.5 ${
      active ? "bg-[#7C3AED] border-[#7C3AED] text-white" : "bg-[#1E1E1E] border-[#27272A] text-[#C4B5FD] hover:text-white"}`}>
    <UsersRound className="h-3 w-3" /> Groups
  </button>
);

const SearchBox = ({ placeholder, onSearch, testid, resetKey }) => {
  const [text, setText] = useState("");
  useEffect(() => { setText(""); }, [resetKey]);
  const submit = (e) => { e.preventDefault(); onSearch(text.trim()); };
  return (
    <form onSubmit={submit} className="relative" data-testid={`${testid}-form`}>
      <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6B7280]" />
      <input data-testid={`${testid}-input`} type="search" enterKeyHint="search" value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder}
        className="w-full h-11 pl-10 pr-10 rounded-full bg-[#1E1E1E] border border-[#4C1D95]/60 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#7C3AED] [&::-webkit-search-cancel-button]:hidden" />
      {text && (
        <button type="button" data-testid={`${testid}-clear`} aria-label="Clear" onClick={() => { setText(""); onSearch(""); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full flex items-center justify-center text-[#9CA3AF] hover:text-white"><X className="h-4 w-4" /></button>
      )}
    </form>
  );
};

const RequestButton = ({ g, onRequested }) => {
  const [busy, setBusy] = useState(false);
  if (g.status === "pending") {
    return <span data-testid={`market-group-pending-${g.group_id}`} className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-[#F59E0B]/15 text-xs text-[#FBBF24]"><Clock className="h-3.5 w-3.5" /> Waiting for approval</span>;
  }
  const request = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      const r = await api.post(`/groups/${g.group_id}/request`);
      toast.success(`Request sent to ${g.name}, the admin needs to approve you`);
      onRequested(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the request");
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={request} disabled={busy} data-testid={`market-group-request-${g.group_id}`}
      className="h-8 px-4 rounded-full bg-[#7C3AED] hover:bg-[#6D28D9] text-xs font-medium text-white inline-flex items-center gap-1.5 disabled:opacity-50 transition-colors">
      {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Request to join
    </button>
  );
};

const DirectoryCard = ({ g, onOpen, onRequested }) => {
  const member = g.status === "active";
  return (
    <div data-testid={`market-group-${g.group_id}`} onClick={member ? onOpen : undefined}
      className={`rounded-2xl bg-[#121212] border border-[#1E1E1E] p-4 flex flex-col gap-3 transition-colors ${member ? "cursor-pointer hover:border-[#7C3AED]/60" : ""}`}>
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 shrink-0 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#4C1D95] flex items-center justify-center font-semibold text-white">{g.name.charAt(0).toUpperCase()}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-[#F3F4F6]">
            <span className="truncate">{g.name}</span>
            {g.is_admin && <Crown className="h-3.5 w-3.5 text-[#F59E0B] shrink-0" />}
            {g.visibility === "private" && <Lock className="h-3 w-3 text-[#9CA3AF] shrink-0" aria-label="Private" />}
          </div>
          <div className="text-[11px] text-[#9CA3AF]">{g.member_count} {g.member_count === 1 ? "member" : "members"}{member ? ` · ${g.note_count} ${g.note_count === 1 ? "note" : "notes"}` : ""}</div>
        </div>
        {member && <ChevronRight className="h-4 w-4 text-[#6B7280]" />}
      </div>
      {g.description && <p className="text-xs text-[#D1D5DB] line-clamp-2">{g.description}</p>}
      {!member && <div className="mt-auto"><RequestButton g={g} onRequested={onRequested} /></div>}
    </div>
  );
};

export const GroupsDirectory = ({ groups, groupId, dirQuery, setGroupId, onGroupUpdated }) => {
  if (groups?.some((g) => g.group_id === groupId && g.status === "active")) return null;
  return (
    <div data-testid="market-groups-directory" className="px-6 pt-4 max-w-6xl mx-auto">
      {!groups ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6B7280]" /></div>
        : !groups.length ? (
          <div data-testid="market-groups-none" className="rounded-2xl border border-dashed border-[#4C1D95] bg-[#7C3AED]/5 px-5 py-8 text-center">
            <div className="text-sm font-medium text-[#F3F4F6] mb-1">{dirQuery ? `No groups match “${dirQuery}”` : "No groups on NoteLosh yet"}</div>
            {!dirQuery && <Link to="/market?community=groups" data-testid="market-groups-manage-link" className="text-xs text-[#C4B5FD] underline">Create the first group</Link>}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {groups.map((g) => <DirectoryCard key={g.group_id} g={g} onOpen={() => setGroupId(g.group_id)} onRequested={onGroupUpdated} />)}
          </div>
        )}
    </div>
  );
};

// Top of the Groups filter: group search, or the open group's header + note search.
export const GroupsBar = ({ groups, groupId, setGroupId, onSearch, setDirQuery }) => {
  useEffect(() => { onSearch(""); }, [groupId]); // eslint-disable-line react-hooks/exhaustive-deps
  const sel = groups?.find((g) => g.group_id === groupId && g.status === "active");
  if (!sel) {
    return (
      <div className="px-6 pt-4 max-w-6xl mx-auto">
        <SearchBox placeholder="Search groups" onSearch={setDirQuery} testid="market-groups-search" resetKey="dir" />
      </div>
    );
  }
  return (
    <div data-testid="market-groups-bar" className="px-6 pt-4 max-w-6xl mx-auto space-y-3">
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => setGroupId(null)} data-testid="market-groups-back-btn" aria-label="All groups"
          className="h-8 pl-2 pr-3 rounded-full border border-[#27272A] text-xs text-[#D1D5DB] hover:text-white flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> All groups</button>
        <span data-testid="market-group-current-name" className="text-sm font-semibold text-[#F3F4F6] truncate">{sel.name}</span>
        <Link to={`/groups/${groupId}`} data-testid="market-group-settings-link" aria-label="Manage group"
          className="shrink-0 h-8 w-8 rounded-full border border-[#27272A] flex items-center justify-center text-[#9CA3AF] hover:text-white"><Settings2 className="h-3.5 w-3.5" /></Link>
      </div>
      <SearchBox placeholder={`Search in ${sel.name}`} onSearch={onSearch} testid="market-group-search" resetKey={groupId} />
    </div>
  );
};
